import { type ComponentType, useEffect, useMemo, useState } from "react";
import cloneDeep from "lodash/cloneDeep";
import pick from "lodash/pick";
import styled, { createGlobalStyle } from "styled-components";
import CollabStatusDropdown from "../CollabStatusDropdown";
import CollabThemeDropdown from "../CollabThemeDropdown";
import {
  AccountMultiSelectDropdown,
  accountTypesToSelectorAccounts,
  accountTypesToSelectorGroups,
} from "../../ui/selectors";
import { ApprovalLink } from "../../../pages/approvals/approval-queries";
import { TextInput } from "../../../ui/ecosystems/forms/inputs";
import { Checkbox, FormSize } from "../../../ui/ecosystems/forms";
import { Alert, AlertType } from "../../../ui/alert";
import { useTrackEvent } from "../../../libs/analytics";
import { AccountType } from "../../../shared/types/accounts";

import PendingIcon from "../../../assets/icons/question-rounded.svg";
import ApprovedIcon from "../../../assets/icons/checked-green-rounded.svg";
import RejectedIcon from "../../../assets/icons/close-red-rounded.svg";
import { COLLABORATION_CREATE_EXT_LINK } from "../../../constants/Events";
import { Button } from "../../ui/button";
import { TooltipWrapper } from "../../../ui";
import { useInstagramApiPublishingOn } from "../../../utils/feature-toggle-utils";

export type ExternalCollabLinkEditProps = {
  link: ApprovalLink;
  accounts: AccountType[];
  onSave: (link: ApprovalLink) => void;
  onCancel: (initialLink: ApprovalLink) => void;
  isEditMode: boolean;
  defaultStatusKey: string;
};

const emailRegex = new RegExp(
  "^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+.[a-zA-Z0-9-.]+$"
);

const CheckboxWrap = styled.div<{ marginBottom?: string }>`
  display: flex;
  margin-left: 10px;
  margin-bottom: ${({ marginBottom }) => marginBottom || "16px"};

  strong {
    font-size: 12px;
  }
`;

const TextInputStyled = styled(TextInput)`
  --inputHeight: 42px;
`;

const MessagesAndCommentsWrap = styled.div<{ isDisabled: boolean }>`
  ${({ isDisabled }) => isDisabled && `opacity: 0.5; margin-top: 10px;`}
`;

const GlobalStyles = createGlobalStyle`
  .tooltip-inner {
    padding: 14px;
    font-weight: 600;
  }
`;

const ExternalCollabLinkEdit = ({
  link,
  accounts,
  onSave,
  onCancel,
  isEditMode,
  defaultStatusKey,
}: ExternalCollabLinkEditProps): JSX.Element => {
  const trackEvent = useTrackEvent();
  const instagramApiPublishing = useInstagramApiPublishingOn();
  const selectorAccounts = useMemo(
    () => accountTypesToSelectorAccounts(accounts, { instagramApiPublishing }),
    [accounts, instagramApiPublishing]
  );
  const selectorGroups = useMemo(
    () => accountTypesToSelectorGroups(accounts, { instagramApiPublishing }),
    [accounts, instagramApiPublishing]
  );
  const [localLink, setlocalLink] = useState<ApprovalLink>(() =>
    cloneDeep(link)
  );
  const unavailableAccountIds = useMemo(() => {
    const availableIds = new Set(accounts.map((account) => account._id));
    return (localLink.accountIds || []).filter(
      (accountId) => !availableIds.has(accountId)
    );
  }, [accounts, localLink.accountIds]);
  const unavailableAccountCount = unavailableAccountIds.length;

  const [emailsOnAccept, setEmailsOnAccept] = useState(
    localLink.notifications?.onAccept.join(", ")
  );
  const [emailsOnReject, setEmailsOnReject] = useState(
    localLink.notifications?.onReject.join(", ")
  );

  const [emailErrors, setEmailErrors] = useState<
    Array<"emailsOnAccept" | "emailsOnReject">
  >([]);

  const updateField = (field: keyof ApprovalLink, value: any) => {
    const copy = cloneDeep(localLink);
    (copy as any)[field] = value;
    setlocalLink(copy);
  };

  const updateReadOnly = (value: boolean) => {
    const copy = cloneDeep(localLink);
    copy.isReadOnly = value;
    // if true, empty out the other states that aren't going to be used
    if (value) {
      copy.rejectState = "";
      copy.acceptState = "";
      copy.isConversationsEnabled = false;
    }
    return setlocalLink(copy);
  };

  useEffect(() => {
    validateEmailsAndUpdateField("emailsOnReject", emailsOnReject);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailsOnReject]);

  useEffect(() => {
    validateEmailsAndUpdateField("emailsOnAccept", emailsOnAccept);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailsOnAccept]);

  const validateEmailsAndUpdateField = (
    field: "emailsOnReject" | "emailsOnAccept",
    value = ""
  ) => {
    const emails = value
      .split(",")
      .map((email) => email.trim())
      .filter((email) => !!email);
    const isEmpty = emails.length === 0;
    const hasFailedEmails = !emails.every((email) => emailRegex.test(email));

    // these fields are optional, so empty value won't result in error
    if (isEmpty || !hasFailedEmails) {
      setEmailErrors(emailErrors.filter((error) => error !== field));
      const notificationField =
        field === "emailsOnAccept" ? "onAccept" : "onReject";
      let { notifications } = localLink;
      if (!notifications) {
        notifications = {
          onAccept: [],
          onReject: [],
        };
      }
      notifications[notificationField] = [...new Set(emails)];
      updateField("notifications", notifications);
    } else {
      if (!emailErrors.includes(field)) setEmailErrors([...emailErrors, field]);
    }
  };

  const handleSave = () => {
    // For new chat (with feature toggle), derive isConversationsEnabled from the granular settings
    // For old chat (without feature toggle), keep the existing value
    const linkToSave = { ...localLink };
    linkToSave.isConversationsEnabled =
      localLink.isStatusChangesVisible !== false ||
      localLink.isExternalNotesVisible !== false ||
      localLink.isPostActivityVisible !== false;

    if (isEditMode && localLink._id !== "NEW_LINK") {
      onSave(linkToSave);
    } else {
      const defaultStatusKeyPairs = {
        showPostWithState: defaultStatusKey,
        acceptState: defaultStatusKey,
        rejectState: defaultStatusKey,
      };

      trackEvent({
        eventName: COLLABORATION_CREATE_EXT_LINK,
        metadata: {
          "read only": !!localLink.isReadOnly,
          review: !!localLink.isReadOnly === false,
        },
      });

      onSave({
        ...defaultStatusKeyPairs,
        ...linkToSave,
      });
    }
  };

  const currentStateValues = Object.values(
    pick(localLink, ["showPostWithState", "acceptState", "rejectState"])
  ).filter(Boolean);
  const hasDuplicateStates =
    new Set(currentStateValues).size !== currentStateValues.length;
  const ifReadOnly =
    localLink.isReadOnly &&
    localLink.showPostWithState &&
    localLink.accountIds &&
    localLink.accountIds.length > 0;
  const saveButtonDisabled =
    !ifReadOnly &&
    (emailErrors.length > 0 ||
      hasDuplicateStates ||
      !localLink.accountIds ||
      localLink.accountIds.length === 0 ||
      !localLink.showPostWithState ||
      !localLink.acceptState ||
      !localLink.rejectState);
  const shouldSelectDefaultStatus = localLink._id === "NEW_LINK" ? false : true;

  return (
    <Container data-testid="addEditExternalLink">
      <GlobalStyles />
      <Title>Set up</Title>
      <TitleDescription>
        Configure the link name, accounts and reviewer
      </TitleDescription>
      <Row>
        <LabelledInput>
          <Label>Link name</Label>
          <TextInputStyled
            placeholder="Link name"
            value={localLink.linkName}
            onChange={(value: string) => {
              updateField("linkName", value);
            }}
          />
        </LabelledInput>

        <LabelledInput>
          <Label>Theme</Label>
          <ThemeInput
            size={FormSize.small}
            onChange={(value: string) => updateField("themeId", value)}
            themeId={localLink.themeId ?? ""}
          />
        </LabelledInput>
      </Row>
      <Row>
        <LabelledInput>
          <Label>Username of reviewer</Label>
          <TextInputStyled
            placeholder="Jane Smith"
            value={localLink.userName}
            onChange={(value: string) => {
              updateField("userName", value);
            }}
          />
        </LabelledInput>
      </Row>
      <Row>
        <FullWidthLabelledInput data-testid="accountsSelectLabel">
          <Label>Accounts:</Label>
          <AccountMultiSelectDropdown
            accounts={selectorAccounts}
            groups={selectorGroups}
            selectedIds={localLink.accountIds || []}
            onChange={(accountIds) => updateField("accountIds", accountIds)}
            className="mb-8"
            aria-label="External collaboration accounts"
          />
          {(localLink.accountIds?.length ?? 0) > 0 && (
            <p className="-mt-6 mb-4 text-xs text-neutral-700">
              {localLink.accountIds.length}{" "}
              {localLink.accountIds.length === 1 ? "account" : "accounts"}{" "}
              selected
            </p>
          )}
          {unavailableAccountCount > 0 && (
            <div
              role="alert"
              className="-mt-2 mb-4 flex items-center justify-between gap-3 rounded-lg border border-orange-200 bg-orange-50 px-3 py-2.5 text-xs text-orange-800"
            >
              <span>
                {unavailableAccountCount} selected{" "}
                {unavailableAccountCount === 1 ? "account is" : "accounts are"}{" "}
                no longer available.
              </span>
              <button
                type="button"
                className="shrink-0 border-0 bg-transparent p-0 font-semibold text-inherit underline underline-offset-2"
                onClick={() => {
                  const unavailableIds = new Set(unavailableAccountIds);
                  updateField(
                    "accountIds",
                    (localLink.accountIds || []).filter(
                      (accountId) => !unavailableIds.has(accountId)
                    )
                  );
                }}
              >
                Remove {unavailableAccountCount === 1 ? "account" : "accounts"}
              </button>
            </div>
          )}
        </FullWidthLabelledInput>
      </Row>
      <Title>Status Configuration</Title>{" "}
      <TitleDescription>
        Configure the status of posts and who receives notification emails
      </TitleDescription>
      <Row>
        <CheckboxWrap>
          <Checkbox checked={!!localLink.isReadOnly} onChange={updateReadOnly}>
            <strong>Read Only (Calendar and Grid View)</strong>
          </Checkbox>
        </CheckboxWrap>
      </Row>
      {localLink.isReadOnly && (
        <Row>
          <Alert>
            Reviewer cannot approve/request changes. Only scheduled/queued posts
            will appear (no draft posts).
          </Alert>
        </Row>
      )}
      <Row>
        <LabelledInput data-testid="pendingStatusInputLabel">
          {localLink.isReadOnly && (
            <Label>
              Reviewer will see posts with the following status only:
            </Label>
          )}
          {!localLink.isReadOnly && (
            <Label>
              <IconWrap>
                <PendingIcon />
              </IconWrap>
              <em>Pending</em>: Posts with this status will be reviewed
            </Label>
          )}

          <StatusInput
            onChange={(value: string) =>
              updateField("showPostWithState", value)
            }
            postStatusKey={localLink.showPostWithState}
            selectDefault={shouldSelectDefaultStatus}
          />
        </LabelledInput>
      </Row>
      {!localLink.isReadOnly && (
        <>
          <Row>
            <LabelledInput data-testid="approvedStatusInputLabel">
              <Label>
                <IconWrap>
                  <ApprovedIcon />
                </IconWrap>
                <em>Approved</em>: Posts will change to this status
              </Label>
              <StatusInput
                onChange={(value: string) => updateField("acceptState", value)}
                postStatusKey={localLink.acceptState}
                selectDefault={shouldSelectDefaultStatus}
              />
            </LabelledInput>
            <Union>and</Union>
            <LabelledInput>
              <Label>Send an email to notify:</Label>
              <TextInputStyled
                placeholder="add multiple emails by adding a “,”"
                value={emailsOnAccept}
                error={emailErrors.includes("emailsOnAccept")}
                onChange={(value: string) => setEmailsOnAccept(value)}
              />
            </LabelledInput>
          </Row>
          <Row>
            <LabelledInput data-testid="changeRequestStatusInputRow">
              <Label>
                <IconWrap>
                  <RejectedIcon />
                </IconWrap>
                <em>Changes requested</em>: Posts will change to this status
              </Label>
              <StatusInput
                onChange={(value: string) => updateField("rejectState", value)}
                postStatusKey={localLink.rejectState}
                selectDefault={shouldSelectDefaultStatus}
              />
            </LabelledInput>
            <Union>and</Union>
            <LabelledInput>
              <Label>Send an email to notify:</Label>
              <TextInputStyled
                placeholder="add multiple emails by adding a “,”"
                value={emailsOnReject}
                error={emailErrors.includes("emailsOnReject")}
                onChange={(value: string) => setEmailsOnReject(value)}
              />
            </LabelledInput>
          </Row>
        </>
      )}
      <MessagesAndCommentsWrap isDisabled={!!localLink.isReadOnly}>
        <Title>Post history and discussion</Title>
        <TitleDescriptionFlex>
          Configure external notes and status changes visibility
        </TitleDescriptionFlex>
        <Row>
          <CheckboxWrap marginBottom="4px">
            <Checkbox
              checked={localLink.isStatusChangesVisible !== false}
              onChange={(value: boolean) => {
                if (!localLink.isReadOnly) {
                  updateField("isStatusChangesVisible", value);
                }
              }}
            >
              <strong>
                Allow status changes to be seen by viewers of this link
              </strong>
            </Checkbox>
          </CheckboxWrap>
        </Row>
        <Row>
          <CheckboxWrap marginBottom="4px">
            <Checkbox
              checked={localLink.isPostActivityVisible !== false}
              onChange={(value: boolean) => {
                if (!localLink.isReadOnly) {
                  updateField("isPostActivityVisible", value);
                }
              }}
            >
              <strong>
                Allow post activities to be seen by viewers of this link
              </strong>
            </Checkbox>
          </CheckboxWrap>
        </Row>
        <Row>
          <CheckboxWrap marginBottom="0px">
            <Checkbox
              checked={localLink.isExternalNotesVisible !== false}
              onChange={(value: boolean) => {
                if (!localLink.isReadOnly) {
                  updateField("isExternalNotesVisible", value);
                }
              }}
            >
              <strong>
                Allow external notes to be seen by viewers of this link
              </strong>
            </Checkbox>
          </CheckboxWrap>
        </Row>
      </MessagesAndCommentsWrap>
      {hasDuplicateStates && (
        <Row>
          <Alert type={AlertType.error}>
            The chosen statuses are invalid, you can only select a status once.
            Please update this link.
          </Alert>
        </Row>
      )}
      <Row content={"flex-end"}>
        <CTAButtonsWrap>
          <CancelButton onClick={() => onCancel(link)}>Cancel</CancelButton>
          <TooltipWrapper
            tooltip={
              saveButtonDisabled
                ? "Please make sure you've added an account and entered in statuses to save this link."
                : null
            }
          >
            <ControlButtonWrap>
              <Button onClick={handleSave} disabled={saveButtonDisabled}>
                Save
              </Button>
            </ControlButtonWrap>
          </TooltipWrapper>
        </CTAButtonsWrap>
      </Row>
    </Container>
  );
};

const Container = styled.div`
  margin-bottom: 30px;
`;

const LabelledInput = styled.div`
  flex: 1;
  max-width: 44%;
  margin-bottom: 6px;
  margin-top: 6px;
`;
const FullWidthLabelledInput = styled(LabelledInput)`
  max-width: 100%;
`;
const Label = styled.p`
  font-weight: 500;
  font-size: 12px;
  line-height: 14px;
  color: #454545;
  margin: 0px 0px 10px;
  display: flex;
  align-items: center;
  white-space: nowrap;
`;

const IconWrap = styled.div`
  display: inline-block;
  border-radius: 50%;
  margin-right: 5px;

  svg {
    width: 18px;
    height: 18px;
  }
`;

const Title = styled.h4`
  font-size: 16px;
  line-height: 19px;
  color: #333333;
  margin-top: 12px;
`;
const TitleDescription = styled.p`
  font-size: 12px;
  line-height: 14px;
  color: #454545;
  margin-bottom: 16px;
`;

const TitleDescriptionFlex = styled(TitleDescription)`
  display: inline-block;
  margin-bottom: 10px;
`;

const Row = styled.div<{ content?: string }>`
  display: flex;
  justify-content: ${({ content }) => content || "space-between"};
  align-items: center;
  margin: 5px 0;
`;

const ThemeInput = styled(CollabThemeDropdown as ComponentType<any>)``;

const StatusInput = styled(CollabStatusDropdown)``;

const Union = styled.p`
  font-weight: 500;
  font-size: 12px;
  line-height: 14px;
  color: #333333;
  margin-top: 32px;
`;

const CTAButtonsWrap = styled.div`
  margin-top: 60px;
  display: flex;
  align-items: center;
`;

const CancelButton = styled.button`
  border: none;
  background: none;
  color: #fa4f38;
  border: 1px solid #fa4f38;
  padding: 13px 30px;
  display: grid;
  place-content: center;
  font-size: 12px;
  font-weight: 400;
  height: 41px;
  width: 123px;
`;

const ControlButtonWrap = styled.div`
  border-radius: 8px;
  margin-left: 16px;

  button {
    font-weight: 500 !important;
    font-size: 12px !important;
    line-height: 125.5% !important;
    height: auto !important;
    padding: 13px 30px;
    width: 123px;
  }
`;

export default ExternalCollabLinkEdit;
